export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'fair' | 'good' | 'strong';
  score: number;
}

export const validatePassword = (password: string): PasswordValidationResult => {
  const errors: string[] = [];
  let score = 0;

  // Length requirements
  if (password.length < 12) {
    errors.push('Password must be at least 12 characters long');
  } else {
    score += 25;
  }

  // Character requirements
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!hasUpperCase) {
    errors.push('Password must contain at least one uppercase letter');
  } else {
    score += 20;
  }

  if (!hasLowerCase) {
    errors.push('Password must contain at least one lowercase letter');
  } else {
    score += 20;
  }

  if (!hasNumbers) {
    errors.push('Password must contain at least one number');
  } else {
    score += 20;
  }

  if (!hasSpecialChar) {
    errors.push('Password must contain at least one special character');
  } else {
    score += 15;
  }

  // Additional strength checks
  if (password.length >= 16) {
    score += 10;
  }

  // Check for common patterns
  const commonPatterns = [
    /123456/,
    /abcdef/,
    /qwerty/,
    /password/i,
    /admin/i,
    /(.)\1{2,}/, // repeated characters
  ];

  const hasCommonPattern = commonPatterns.some(pattern => pattern.test(password));
  if (hasCommonPattern) {
    errors.push('Password contains common patterns that make it weak');
    score -= 20;
  }

  // Determine strength
  let strength: 'weak' | 'fair' | 'good' | 'strong' = 'weak';
  if (score >= 90) strength = 'strong';
  else if (score >= 70) strength = 'good';
  else if (score >= 50) strength = 'fair';

  return {
    isValid: errors.length === 0 && score >= 70,
    errors,
    strength,
    score: Math.max(0, Math.min(100, score))
  };
};

export const getPasswordStrengthColor = (strength: string): string => {
  switch (strength) {
    case 'strong': return 'text-green-600';
    case 'good': return 'text-blue-600';
    case 'fair': return 'text-yellow-600';
    default: return 'text-red-600';
  }
};

export const getPasswordStrengthBg = (strength: string): string => {
  switch (strength) {
    case 'strong': return 'bg-green-500';
    case 'good': return 'bg-blue-500';
    case 'fair': return 'bg-yellow-500';
    default: return 'bg-red-500';
  }
};